"use client";

import Link from "next/link";
import { ArrowUpRight, CheckCircle2, ShieldCheck, Zap, Database, Server, Clock, BarChart3, ArrowRight } from "lucide-react";

export default function SanadcomAEPage() {
  return (
    <main className="relative z-10 flex-grow container mx-auto max-w-4xl px-6 py-20 pt-24" style={{ paddingTop: '6rem' }}>
      <Link
        href="/#projects"
        className="text-slate-500 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-300 font-mono text-sm inline-flex items-center gap-2 transition-colors mb-8"
      >
        &larr; Back to Case Studies
      </Link>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="tag font-mono">FINTECH</span>
        <span className="tag font-mono">EDGE ARCHITECTURE</span>
        <span className="tag font-mono">SAAS</span>
      </div>

      <h1 className="font-mono mb-4 text-4xl font-bold md:text-6xl text-slate-900 dark:text-white tracking-tight">
        Sanadcom AE
      </h1>
      <p className="font-mono text-lg text-purple-600 dark:text-purple-400 mb-6">
        Architecting an Edge-Native Compliance & Invoicing Platform for Moroccan Entrepreneurs
      </p>

      <p className="mb-10 text-lg md:text-xl text-slate-600 dark:text-slate-400 leading-relaxed">
        Sanadcom AE is an end-to-end fintech SaaS engineered by 52S Solutions specifically for independent professionals and micro-enterprises operating under the Moroccan Auto-Entrepreneur statutory regime. It eliminates administrative friction by combining sub-second invoice generation, automatic fiscal ceiling tracking, and quarterly declaration readiness into a unified, high-performance interface.
      </p>

      {/* ── Key Metrics Grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-14">
        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
            <Clock className="w-5 h-5" />
            <span className="font-mono text-xs font-bold uppercase tracking-wider">Speed</span>
          </div>
          <div className="font-mono text-3xl font-extrabold text-slate-900 dark:text-white mb-1">&lt; 30s</div>
          <p className="text-sm text-slate-600 dark:text-slate-400">Average time to generate and export legal, tax-compliant PDF invoices.</p>
        </div>

        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
            <Zap className="w-5 h-5" />
            <span className="font-mono text-xs font-bold uppercase tracking-wider">Latency</span>
          </div>
          <div className="font-mono text-3xl font-extrabold text-slate-900 dark:text-white mb-1">&lt; 150ms</div>
          <p className="text-sm text-slate-600 dark:text-slate-400">P95 dashboard response time on domestic mobile networks via Edge caching.</p>
        </div>

        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-mono text-xs font-bold uppercase tracking-wider">Compliance</span>
          </div>
          <div className="font-mono text-3xl font-extrabold text-slate-900 dark:text-white mb-1">100%</div>
          <p className="text-sm text-slate-600 dark:text-slate-400">Strict adherence to Moroccan statutory turnover ceilings and tax schedules.</p>
        </div>
      </div>

      {/* ── The Problem Section ── */}
      <h2 className="font-mono text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">
        The Problem: Regulatory Drag on Independent Growth
      </h2>
      <p className="text-lg text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
        The Auto-Entrepreneur program in Morocco provides a streamlined legal status for independent contractors, but introduces rigid fiscal constraints: strict annual turnover limits (200,000 MAD for service providers and 500,000 MAD for commercial/industrial activities), quarterly tax filing requirements, and specific invoicing mandates.
      </p>
      <p className="text-lg text-slate-600 dark:text-slate-400 mb-10 leading-relaxed">
        Prior to Sanadcom AE, entrepreneurs relied on disconnected spreadsheets or generic European invoicing tools that failed to account for local tax thresholds, ICE (Identifiant Commun de l’Entreprise) requirements, and tax declaration cycles. This led to miscalculated tax provisions, missed declaration deadlines, and constant risk of penalty fees.
      </p>

      {/* ── Engineering Architecture Section ── */}
      <h2 className="font-mono text-2xl md:text-3xl font-bold mb-4 text-slate-900 dark:text-white">
        The Engineering Architecture
      </h2>
      <p className="text-lg text-slate-600 dark:text-slate-400 mb-6 leading-relaxed">
        To deliver instantaneous performance across diverse device types in Morocco—from low-bandwidth mobile connections in regional hubs to fiber networks in Casablanca—we engineered Sanadcom AE with an edge-first philosophy:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2 text-purple-600 dark:text-purple-400">
            <Server className="w-5 h-5" />
            <h3 className="font-mono font-bold text-slate-900 dark:text-white text-base">Edge Execution & SSR</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Constructed with Next.js App Router and deployed across Cloudflare edge nodes. Core business routes render at the nearest point of presence, eliminating transatlantic network roundtrips.
          </p>
        </div>

        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2 text-purple-600 dark:text-purple-400">
            <Database className="w-5 h-5" />
            <h3 className="font-mono font-bold text-slate-900 dark:text-white text-base">Resilient Data Persistence</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Structured relational database backends ensuring strict ACID compliance, tenant data isolation, encrypted records, and zero-data-loss transaction logging.
          </p>
        </div>

        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2 text-purple-600 dark:text-purple-400">
            <BarChart3 className="w-5 h-5" />
            <h3 className="font-mono font-bold text-slate-900 dark:text-white text-base">Automated Threshold Telemetry</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Dynamic threshold tracking algorithms compute cumulative revenue against legal ceilings in real time, alerting entrepreneurs when nearing limit thresholds to avoid reclassification.
          </p>
        </div>

        <div className="p-6 rounded-xl bg-slate-50/80 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/[0.08] backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2 text-purple-600 dark:text-purple-400">
            <Zap className="w-5 h-5" />
            <h3 className="font-mono font-bold text-slate-900 dark:text-white text-base">Instant Document Rendering</h3>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
            Lightweight, vector-optimized PDF generation engine producing branded, legally compliant quotes (devis) and invoices (factures) with zero client-side lag.
          </p>
        </div>
      </div>

      {/* ── Related Services Callout ── */}
      <div className="p-6 rounded-xl bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/80 dark:border-purple-800/30 backdrop-blur-sm mb-12">
        <span className="text-xs font-mono text-purple-600 dark:text-purple-400 uppercase tracking-wider block mb-1 font-bold">52S Engineering Capabilities</span>
        <h4 className="font-mono text-lg font-bold text-slate-900 dark:text-white mb-2">
          Building a specialized SaaS or high-load customer portal?
        </h4>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
          From fintech compliance logic to distributed database design, our engineering teams architect custom products built for long-term scale.
        </p>
        <div className="flex flex-wrap gap-4 text-sm font-mono font-bold">
          <Link
            href="/services/saas-mvp-development"
            className="text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center gap-1"
          >
            SaaS MVP Development &rarr;
          </Link>
          <Link
            href="/services/web-mobile-apps"
            className="text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center gap-1"
          >
            Explore Web & Mobile Apps &rarr;
          </Link>
          <Link
            href="/services/backend-systems"
            className="text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center gap-1"
          >
            Explore Backend Systems &rarr;
          </Link>
          <Link
            href="/blog/cost-to-build-saas-mvp-2026"
            className="text-purple-600 dark:text-purple-400 hover:underline inline-flex items-center gap-1"
          >
            SaaS MVP Cost Guide &rarr;
          </Link>
        </div>
      </div>

      {/* ── CTAs ── */}
      <div className="flex flex-wrap gap-4">
        <a
          href="https://sanadcomae.online/"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary font-mono px-8 py-3 text-base uppercase tracking-wider inline-flex items-center gap-2"
        >
          Open Sanadcom AE
          <ArrowUpRight className="w-4 h-4" />
        </a>
        <Link
          href="/#contact"
          className="btn btn-outline font-mono px-8 py-3 text-base uppercase tracking-wider inline-flex items-center gap-2"
        >
          Discuss Your SaaS Architecture
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </main>
  );
}